export enum CompanyInfoFeedbackType {
  AlternativeCompany = "Alternative Company",
  Category = "Category",
  Certification = "Certification",
  Headquarters = "Headquarters",
  Logo = "Logo",
  Name = "Name",
  ParentAffiliation = "Parent Affiliation",
  Segment = "Segment",
  Values = "Values",
  Website = "Website",
  Other = "Other"
}

export type CompanyInfoFeedbackCategoryContext = {
  website?: string | null
  hasBrands?: boolean
  certificationCount?: number
  alternativeCompanyCount?: number
}

export type CompanyInfoFeedbackCategoryOption = {
  value: CompanyInfoFeedbackType
  label: string
}

export const BANNO_COMPANY_INFO_FEEDBACK_TYPES = [
  CompanyInfoFeedbackType.Category,
  CompanyInfoFeedbackType.Headquarters,
  CompanyInfoFeedbackType.Logo,
  CompanyInfoFeedbackType.Name,
  CompanyInfoFeedbackType.Segment,
  CompanyInfoFeedbackType.Values,
  CompanyInfoFeedbackType.Website,
  CompanyInfoFeedbackType.Other,
] as const

export type BannoCompanyInfoFeedbackCategoryContext = {
  website?: string | null
}

export function buildBannoCompanyInfoFeedbackCategoryOptions(
  ctx: BannoCompanyInfoFeedbackCategoryContext = {}
): CompanyInfoFeedbackCategoryOption[] {
  const website = typeof ctx.website === 'string' ? ctx.website.trim() : ''

  const cats: Partial<Record<CompanyInfoFeedbackType, string>> = {
    [CompanyInfoFeedbackType.Category]: 'Category is incorrect',
    [CompanyInfoFeedbackType.Headquarters]: 'Headquarters is incorrect or missing',
    [CompanyInfoFeedbackType.Logo]: 'Logo is incorrect or missing',
    [CompanyInfoFeedbackType.Name]: 'Name is incorrect',
    [CompanyInfoFeedbackType.Segment]: 'Segment is incorrect',
    [CompanyInfoFeedbackType.Values]: 'Values are not accurate',
    [CompanyInfoFeedbackType.Website]: `Website is ${website ? 'not active or is incorrect' : 'missing'}`,
    [CompanyInfoFeedbackType.Other]: 'Other',
  }

  return BANNO_COMPANY_INFO_FEEDBACK_TYPES
    .map((value) => ({
      value,
      label: String(cats[value] || value),
    }))
    .sort((a, b) => {
      if (a.label === 'Other') return 1
      if (b.label === 'Other') return -1
      return a.label.localeCompare(b.label)
    })
}

export function resolveBannoCompanyInfoFeedbackCategories(
  categories: CompanyInfoFeedbackCategoryOption[]
): CompanyInfoFeedbackCategoryOption[] {
  const allowed = new Set<string>(BANNO_COMPANY_INFO_FEEDBACK_TYPES)
  return categories.filter((category) => allowed.has(category.value))
}

export function buildCompanyInfoFeedbackCategoryOptions(
  ctx: CompanyInfoFeedbackCategoryContext
): CompanyInfoFeedbackCategoryOption[] {
  const website = typeof ctx.website === 'string' ? ctx.website.trim() : ''
  const certificationCount = Number.isFinite(ctx.certificationCount)
    ? Math.max(0, ctx.certificationCount as number)
    : 0
  const alternativeCompanyCount = Number.isFinite(ctx.alternativeCompanyCount)
    ? Math.max(0, ctx.alternativeCompanyCount as number)
    : 0

  const cats: Partial<Record<CompanyInfoFeedbackType, string>> = {
    [CompanyInfoFeedbackType.Category]: 'Category is incorrect',
    [CompanyInfoFeedbackType.Certification]: `Certification is ${certificationCount === 0 ? 'missing' : 'incorrect or missing'}`,
    [CompanyInfoFeedbackType.Headquarters]: 'Headquarters is incorrect or missing',
    [CompanyInfoFeedbackType.Logo]: 'Logo is incorrect or missing',
    [CompanyInfoFeedbackType.Name]: 'Name is incorrect',
    [CompanyInfoFeedbackType.ParentAffiliation]: `Corporate parent affiliation is ${ctx.hasBrands ? 'incorrect or missing' : 'missing'}`,
    [CompanyInfoFeedbackType.Segment]: 'Segment is incorrect',
    [CompanyInfoFeedbackType.Values]: 'Values are not accurate',
    [CompanyInfoFeedbackType.Website]: `Website is ${website ? 'not active or is incorrect' : 'missing'}`,
    [CompanyInfoFeedbackType.Other]: 'Other',
  }

  if (alternativeCompanyCount > 0) {
    cats[CompanyInfoFeedbackType.AlternativeCompany] = 'Alternative Company is incorrect'
  }

  return Object.entries(cats)
    .map(([value, label]) => ({
      value: value as CompanyInfoFeedbackType,
      label: String(label),
    }))
    .sort((a, b) => {
      if (a.label === 'Other') return 1
      if (b.label === 'Other') return -1
      return a.label.localeCompare(b.label)
    })
}
